import { ArrowLeft, CheckCircle, Clock, Shield, Star } from "lucide-react";
import { useEffect, useState } from "react";
import OptimizedImage from "../../../components/OptimizedImage";
import styles from "../../../styles/OfferDisplay.module.css";

const OfferDisplay = ({ device, onBack, onStartOver, goToNextStep, onChange, formData, step }) => {
    const variants = device?.items?.map((v) => v.primary) ?? [];
    // Initialize from formData if available, otherwise use defaults
    const [selectedVariant, setSelectedVariant] = useState(
        formData?.["Primary Variant"] || variants[0] || ""
    );
    const [selectedPricing, setSelectedPricing] = useState(
        formData?.["Condition"] || ""
    );

    const variantData = device?.items?.find((v) => v.primary === selectedVariant);
    const PricingTiers =
        variantData?.prices?.reduce((acc, tier) => {
            acc[tier.name] = tier.price;
            return acc;
        }, {}) ?? {};

    const selectedPrice = PricingTiers[selectedPricing] ?? 0;
    
    // Reset variant and condition when device changes (e.g., when a new product is selected)
    useEffect(() => {
        if (device) {
            const newVariants = device.items?.map((v) => v.primary) ?? [];
            const formDataVariant = formData?.["Primary Variant"];
            
            // Check if current variant is valid for the new device
            const isCurrentVariantValid = selectedVariant && newVariants.includes(selectedVariant);
            const isFormDataVariantValid = formDataVariant && newVariants.includes(formDataVariant);
            
            // If current variant is not valid for new device, reset it
            if (!isCurrentVariantValid) {
                // Use formData variant if it's valid, otherwise use first available
                const defaultVariant = isFormDataVariantValid ? formDataVariant : (newVariants[0] || "");
                if (defaultVariant !== selectedVariant) {
                    setSelectedVariant(defaultVariant);
                    // Update formData if we're using a default variant (not from formData)
                    if (defaultVariant && onChange && !isFormDataVariantValid) {
                        onChange("Primary Variant", defaultVariant);
                    }
                }
                // Reset condition/offer when device changes or variant becomes invalid for the new device
                setSelectedPricing("");
                if (onChange) {
                    onChange("Condition", null);
                    onChange("Offer Price", null);
                }
            }

            // Even if the variant stays valid, pricing tiers can differ by product.
            // Ensure we don't carry condition/offer across products.
            if (isCurrentVariantValid) {
                setSelectedPricing("");
                if (onChange) {
                    onChange("Condition", null);
                    onChange("Offer Price", null);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device?.id, device?.productName]); // React to device changes only
    
    // Sync state with formData when it changes externally
    useEffect(() => {
        const formDataVariant = formData?.["Primary Variant"];
        const formDataCondition = formData?.["Condition"];
        
        if (formDataVariant !== undefined && formDataVariant !== selectedVariant) {
            // If formData has a variant, use it (even if null/empty)
            if (formDataVariant) {
                setSelectedVariant(formDataVariant);
            } else if (device) {
                // If formData variant is cleared, reset to first available
                const newVariants = device.items?.map((v) => v.primary) ?? [];
                const defaultVariant = newVariants[0] || "";
                setSelectedVariant(defaultVariant);
                if (defaultVariant && onChange) {
                    onChange("Primary Variant", defaultVariant);
                }
            }
        }
        if (formDataCondition !== undefined && formDataCondition !== selectedPricing) {
            setSelectedPricing(formDataCondition || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.["Primary Variant"], formData?.["Condition"]]); // Only react to formData changes
    
    // Save initial variant to formData if it exists and isn't already saved
    // This handles the case when there's only one variant (no selector shown)
    useEffect(() => {
        if (device && selectedVariant && onChange && !formData?.["Primary Variant"]) {
            onChange("Primary Variant", selectedVariant);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device?.id, selectedVariant]); // Only run when device or variant changes, not formData/onChange to avoid loops
    
    // Business info can be added here in the future for contact details
    
    // Use fixed colors for consistent offer display styling
    const primaryColor = "#007bff";
    const backgroundColor = "transparent";
    const textColor = "#181818";
    const cardBackgroundColor = "#ffffff";

    return (
        <div className={styles.wrapper} style={{ backgroundColor }}>
            {device && !Array.isArray(device.items) ? (
                <div className={styles.errorCard} style={{ backgroundColor: cardBackgroundColor }}>
                    <h2 className={styles.errorTitle} style={{ color: textColor }}>Loading offer...</h2>
                </div>
            ) : !device || !device.items ? (
                <div className={styles.errorCard} style={{ backgroundColor: cardBackgroundColor }}>
                    <div className={styles.errorIcon}>
                        <Shield size={48} />
                    </div>
                    <h2 className={styles.errorTitle} style={{ color: textColor }}>Product Not Found</h2>
                    <p className={styles.errorMessage}>
                        We couldn't load the selected product. Please go back and try again.
                    </p>
                    <button onClick={onStartOver} className={styles.btnSecondary}>
                        <ArrowLeft size={16} />
                        Start Over
                    </button>
                </div>
            ) : (
                <>
                    {/* Navigation */}
                    <div className={styles.navigationSection}>
                        <button onClick={onBack} className={styles.backButton}>
                            <ArrowLeft size={20} />
                            Back to Selection
                        </button>
                    </div>

                    <div className={styles.grid}>
                        {/* Configuration Panel */}
                        <div className={styles.configCard} style={{ backgroundColor: cardBackgroundColor }}>
                            <div className={styles.productHeader}>
                                {device.imageUrl && (
                                    <OptimizedImage 
                                        src={device.imageUrl} 
                                        alt={device.productName} 
                                        className={styles.productImage}
                                        lazy={true}
                                    />
                                )}
                                <div className={styles.productInfo}>
                                    <h3 className={styles.productName} style={{ color: textColor }}>
                                        {device.productName}
                                    </h3>
                                    <div className={styles.productCategory}>
                                        {device.category}
                                    </div>
                                </div>
                            </div>

                            {/* Variant Selector */}
                            {variants.length > 0 && (
                                <div className={styles.configSection}>
                                    <label className={styles.sectionLabel}>{device?.primaryLabel || "Storage Capacity"}</label>
                                    <div className={styles.variantGrid}>
                                        {variants.map((variant) => (
                                            <button
                                                key={variant}
                                                onClick={() => {
                                                    setSelectedVariant(variant);
                                                    setSelectedPricing("");
                                                    if (onChange) {
                                                        onChange("Primary Variant", variant);
                                                        // Variant change invalidates any previous condition/offer selection
                                                        onChange("Condition", null);
                                                        onChange("Offer Price", null);
                                                    }
                                                }}
                                                className={`${styles.variantButton} ${selectedVariant === variant ? styles.active : ""}`}
                                                style={{
                                                    borderColor: selectedVariant === variant ? primaryColor : '#e5e7eb',
                                                    backgroundColor: selectedVariant === variant ? `${primaryColor}15` : 'transparent',
                                                    color: selectedVariant === variant ? primaryColor : textColor
                                                }}
                                            >
                                                {variant}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Condition Selector */}
                            <div className={styles.configSection}>
                                <label className={styles.sectionLabel}>Device Condition</label>
                                <div className={styles.conditionGrid}>
                                    {Object.entries(PricingTiers)
                                        .filter(([priceLabel]) => priceLabel !== "Selling")
                                        .map(([priceLabel, price]) => (
                                            <button
                                                key={priceLabel}
                                                onClick={() => {
                                                    setSelectedPricing(priceLabel);
                                                    if (onChange) {
                                                        onChange("Condition", priceLabel);
                                                        onChange("Offer Price", price);
                                                    }
                                                }}
                                                className={`${styles.conditionButton} ${selectedPricing === priceLabel ? styles.active : ""}`}
                                                style={{
                                                    borderColor: selectedPricing === priceLabel ? primaryColor : '#e5e7eb',
                                                    backgroundColor: selectedPricing === priceLabel ? `${primaryColor}15` : 'transparent'
                                                }}
                                            >
                                                <div className={styles.conditionContent}>
                                                    <div className={styles.conditionLabel}>{priceLabel}</div>
                                                    <div className={styles.conditionPrice} style={{ color: primaryColor }}>
                                                        R{price.toLocaleString()}
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                </div>
                            </div>
                        </div>

                        {/* Offer Summary */}
                        <div className={styles.offerCard} style={{ backgroundColor: cardBackgroundColor }}>
                            <div className={styles.offerHeader}>
                                <div className={styles.offerIcon} style={{ backgroundColor: `${primaryColor}15` }}>
                                    <CheckCircle size={32} style={{ color: primaryColor }} />
                                </div>
                                <h3 className={styles.offerTitle} style={{ color: textColor }}>
                                    Your Instant Offer
                                </h3>
                            </div>

                            {selectedPrice > 0 ? (
                                <>
                                    <div className={styles.priceDisplay}>
                                        <div className={styles.priceAmount} style={{ color: primaryColor }}>
                                            R{selectedPrice.toLocaleString()}
                                        </div>
                                        <div className={styles.priceNote}>
                                            Valid for 7 days
                                        </div>
                                    </div>

                                    <div className={styles.offerDetails}>
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Product:</span>
                                            <span className={styles.detailValue}>{device.productName}</span>
                                        </div>
                                        {selectedVariant && (
                                            <div className={styles.detailItem}>
                                                <span className={styles.detailLabel}>{device?.primaryLabel || "Storage"}:</span>
                                                <span className={styles.detailValue}>{selectedVariant}</span>
                                            </div>
                                        )}
                                        {selectedPricing && selectedPricing !== "Selling" && (
                                            <div className={styles.detailItem}>
                                                <span className={styles.detailLabel}>Condition:</span>
                                                <span className={styles.detailValue}>{selectedPricing}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.benefits}>
                                        <div className={styles.benefitItem}>
                                            <Star size={16} style={{ color: primaryColor }} />
                                            <span>Instant valuation</span>
                                        </div>
                                        <div className={styles.benefitItem}>
                                            <Shield size={16} style={{ color: primaryColor }} />
                                            <span>Secure transaction</span>
                                        </div>
                                        <div className={styles.benefitItem}>
                                            <Clock size={16} style={{ color: primaryColor }} />
                                            <span>Quick payment</span>
                                        </div>
                                    </div>

                                    <div className={styles.nextSteps}>
                                        <h4 className={styles.stepsTitle}>What happens next?</h4>
                                        <div className={styles.stepsList}>
                                            <div className={styles.stepItem}>
                                                <div className={styles.stepNumber}>1</div>
                                                <span>Contact us to confirm your quote</span>
                                            </div>
                                            <div className={styles.stepItem}>
                                                <div className={styles.stepNumber}>2</div>
                                                <span>Bring your device for inspection</span>
                                            </div>
                                            <div className={styles.stepItem}>
                                                <div className={styles.stepNumber}>3</div>
                                                <span>Get paid fast once your device passes inspection</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.actions}>
                                        <button 
                                            onClick={goToNextStep} 
                                            className={styles.btnPrimary}
                                            style={{ backgroundColor: primaryColor }}
                                        >
                                            Accept This Offer
                                        </button>
                                        
                                        <button onClick={onStartOver} className={styles.btnSecondary}>
                                            Get Quote for Another Item
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className={styles.noSelection}>
                                    <p>Please select a condition to see your offer</p>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );

};

export default OfferDisplay;
